import { CustomTable, TableColumn } from '@/components/Table';
import User from '@/model/User';
import UserType from '@/model/UserType';
import React, { ChangeEvent } from 'react';
import { ReactComponent as Pen } from '@/assets/images/svg/pen.svg';
import { ReactComponent as Trash } from '@/assets/images/svg/lixeira.svg';
import { ReactComponent as RecoverPassword } from '@/assets/images/svg/reload-password.svg';
import { Input } from 'reactstrap';
import Profile from '@/model/Profile';
import { DropdonwFlags } from '@/components';
import StatusType from '@/model/StatusType';
import { updateMask } from '@/helpers/masks/cpf';
import { CheckBoxUser, ShouldShowModal } from '..';

interface DataTable {
  checkbox: string;
  name: string;
  cpf: string;
  userType: UserType;
  profiles: string;
  status: string;
  actions: string;
}

interface DataColumn {
  id: string;
  name: string;
}

interface StateProps {
  count: number;
  users: CheckBoxUser[];
}
interface DispatchProps {
  checkAll(e: ChangeEvent<HTMLInputElement>): void;
  change(e: React.ChangeEvent<HTMLInputElement>, user: CheckBoxUser): void;
  toUserType(userType: UserType): string;
  openModal(value: ShouldShowModal, modalTitle: string, user?: User, group?: Profile): void;
  showDelete(user: User): void;
  onReloadPassword(id: string): void;
}

type Props = StateProps & DispatchProps;

export const UserList: React.FC<Props> = (props: Props): JSX.Element => {
  const userTypeToString = (userType: UserType): string => {
    let value = '';
    switch (userType) {
      case UserType.Employee:
        value = 'Funcionários';
        break;
      case UserType.PDV:
        value = 'PDV';
        break;
      case UserType.SUB_PDV:
        value = 'SubPDV';
        break;
      case UserType.POS:
        value = 'POS';
        break;
      case UserType.CONTRACTOR:
        value = 'Produtor';
        break;
      case UserType.VALIDATOR:
        value = 'Validador';
        break;
      case UserType.ARTIST:
        value = 'Artista';
        break;
      default:
        value = 'Funcionários';
        break;
    }
    return value;
  };
  const checkBoxAll = (): React.ReactNode => (
    <div className="checkFieldSpacing">
      <Input
        type="checkbox"
        checked={props.count === props.users.length}
        onChange={e => props.checkAll(e)}
      />
    </div>
  );

  const checkBox = (user: CheckBoxUser): React.ReactNode => (
    <div className="checkFieldSpacing">
      <Input
        type="checkbox"
        value={user.check}
        checked={user.check === 'true'}
        onChange={e => props.change(e, user)}
      />
    </div>
  );

  const dataTable = props.users?.map(user => {
    const dataColumnProfiles: DataColumn[] = [];
    if (user.profiles && user.profiles.length > 0) {
      user.profiles.forEach(data => {
        dataColumnProfiles.push({
          id: data.id,
          name: data.name,
        });
      });
    }

    const dataColumnUserTypes: DataColumn[] = [];
    if (user.types && user.types.length > 0) {
      user.types.forEach(data => {
        dataColumnUserTypes.push({
          id: `${Number(data)}`,
          name: userTypeToString(data),
        });
      });
    }

    return {
      checkbox: checkBox(user),
      name: user.name,
      cpf: updateMask(user.cpf),
      userType:
        // eslint-disable-next-line no-nested-ternary
        user.types && user.types.length > 1 ? (
          <DropdonwFlags pointerClass={true} dataColumn={dataColumnUserTypes} />
        ) : user.types && user.types.length === 1 ? (
          userTypeToString(user.types[0])
        ) : (
          ''
        ),
      profiles:
        // eslint-disable-next-line no-nested-ternary
        user.profiles && user.profiles.length > 1 ? (
          <DropdonwFlags pointerClass={true} dataColumn={dataColumnProfiles} />
        ) : user.profiles && user.profiles.length === 1 ? (
          user.profiles[0].name
        ) : (
          ''
        ),
      status:
        user.status === StatusType.ACTIVE ? (
          <div className="flag-item text-active action-icon">Ativo</div>
        ) : (
          <div className="flag-item text-inactive action-icon">Inativo</div>
        ),
      actions: (
        <div className="actions-cell">
          <RecoverPassword
            onClick={(): void => props.onReloadPassword(user.id)}
            className="mr-2 table-action-icon"
          />
          <Pen
            onClick={(): void => props.openModal(ShouldShowModal.user, 'Editar usuário', user)}
            className="mr-2 table-action-icon"
          />
          <Trash onClick={(): void => props.showDelete(user)} className="mr-2 table-action-icon" />
        </div>
      ),
    };
  });

  const columns: TableColumn<DataTable>[] = [
    {
      name: checkBoxAll(),
      selector: row => row.checkbox,
      width: '50px',
    },
    {
      name: 'Nome',
      selector: row => row.name,
    },
    {
      name: 'CPF',
      selector: row => row.cpf,
    },
    {
      name: 'Tipo do usuário',
      selector: row => row.userType,
      cell: row => row.userType,
    },
    {
      name: 'Grupo',
      selector: row => row.profiles,
      cell: row => row.profiles,
    },
    {
      name: 'Situação',
      selector: row => row.status,
    },
    {
      name: 'Ações',
      selector: row => row.actions,
      width: '115px',
    },
  ];

  return (
    <CustomTable
      columns={columns}
      data={dataTable}
      numberRowsPerPage={props.users.length}
      progressPending={false}
    />
  );
};
